import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { api } from '@/lib/api'
import { getAdminToken } from '@/lib/admin-store'
import { getAdminNavLinks, readAdminFiltersFromSearch, type AdminFilters } from './-admin-utils'
import styles from './admin.module.css'

export const Route = createFileRoute('/admin/users/$userId')({
  component: AdminUserJourneyRoute,
})

function AdminUserJourneyRoute() {
  const { userId } = Route.useParams()
  const [token] = useState(getAdminToken)
  const [filters] = useState<AdminFilters>(readAdminFiltersFromSearch)
  const [journey, setJourney] = useState<Awaited<ReturnType<typeof api.adminUserJourney>> | null>(
    null
  )
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const navLinks = useMemo(() => getAdminNavLinks(filters), [filters])

  useEffect(() => {
    const load = async (): Promise<void> => {
      if (!token) {
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const data = await api.adminUserJourney(token, userId)
        setJourney(data)
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Failed to load user journey.')
      } finally {
        setIsLoading(false)
      }
    }

    void load()
  }, [token, userId])

  const backHref = useMemo(() => {
    const qs = typeof window !== 'undefined' ? window.location.search : ''
    return `/admin/users${qs}`
  }, [])

  return (
    <section className={styles.layout}>
      <header className={styles.hero}>
        <h1 className={styles.title}>User Journey</h1>
        <p className={styles.subtitle}>
          Timeline for user <strong>{userId}</strong>.
        </p>
      </header>

      <section className={styles.card}>
        <div className={styles.tabs}>
          {navLinks.map((link) => (
            <a key={link.path} href={link.href} className={styles.tabLink}>
              {link.label}
            </a>
          ))}
        </div>
      </section>

      {!token && (
        <section className={styles.card}>
          <p className={styles.emptyState}>You don't have admin access.</p>
          <a className={styles.tabLink} href="/admin">
            Go to Home
          </a>
        </section>
      )}

      {token && isLoading && (
        <section className={styles.card}>
          <p className={styles.muted}>Loading journey...</p>
        </section>
      )}

      {token && error && (
        <section className={styles.card}>
          <p className={styles.error}>{error}</p>
          <button
            className={styles.secondaryButton}
            type="button"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </section>
      )}

      {token && !isLoading && !error && journey && (
        <section className={styles.card}>
          <p className={styles.muted}>Lifecycle: {journey.lifecycleState}</p>
          {journey.timeline.length === 0 ? (
            <>
              <p className={styles.emptyState}>No timeline events yet.</p>
              <a className={styles.tabLink} href={backHref}>
                Back to Users
              </a>
            </>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {journey.timeline.map((eventRow) => (
                  <tr key={`${eventRow.event}_${eventRow.at}`}>
                    <td>{eventRow.event}</td>
                    <td>{eventRow.at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </section>
  )
}
